import type {
  FetchResult,
  NextLink,
  Observable,
  Operation,
} from '@apollo/client/core';
import { ApolloLink } from '@apollo/client/core';
import { reportError } from '@frameio/telemetry';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';
import { auth, hasAccessTokenExpired } from '@frameio/utils/auth';
import { CLIENT_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS } from '@frameio/utils/auth/constants';
import { isServer, isWorker } from '@frameio/utils/config/helpers';
import { OperationQueue } from './OperationQueue';

let isCurrentlyRefreshingAuthToken = false;
export class AuthRefreshLink extends ApolloLink {
  private queue: OperationQueue;

  constructor() {
    super();

    this.queue = new OperationQueue();
  }

  private handleBrowserOnline = (): void => {
    window.removeEventListener('online', this.handleBrowserOnline);

    if (!hasAccessTokenExpired(auth.getAccessToken())) {
      // the token is valid so we can consume the queue
      this.queue.consumeQueue();
    } else {
      this.fetchAccessTokenThenConsumeQueue();
    }
  };

  private async fetchAccessTokenThenConsumeQueue(): Promise<void> {
    isCurrentlyRefreshingAuthToken = true;
    try {
      await auth.getAccessTokenWithRefreshIfExpired(
        CLIENT_SIDE_ACCESS_TOKEN_EXPIRATION_BUFFER_SECONDS
      );
      this.queue.consumeQueue();

      if (!isWorker) {
        // Used to verify WEB-1829
        datadogLogger.warn('unauthenticated_request', {
          location: 'AuthRefreshLink',
        });
      }
    } catch (error) {
      reportError(error, { message: 'Failed to refresh auth token' });
    }
    isCurrentlyRefreshingAuthToken = false;
  }

  public request(
    operation: Operation,
    forward: NextLink
  ): Observable<FetchResult> | null {
    if (typeof forward !== 'function') {
      throw new Error(
        'AuthRefreshLink is a non-terminating link and should not be last in the composed chain'
      );
    }
    const isUXPRequest = operation.getContext()?.resolveWithUXP;

    if (!isUXPRequest && !isServer && !isWorker && !window?.navigator?.onLine) {
      window.addEventListener('online', this.handleBrowserOnline);
      // if the browser is offline, we should queue up the operations
      // to be consumed when the "online" event is triggered
      return this.queue.enqueueRequest({
        operation,
        forward,
      });
    }

    const hasTokenExpired = hasAccessTokenExpired(auth.getAccessToken());

    if (!hasTokenExpired || isUXPRequest) {
      // the token is valid so we can continue to other links in the chain
      return forward(operation);
    }

    if (!isCurrentlyRefreshingAuthToken) {
      this.fetchAccessTokenThenConsumeQueue();
    }

    return this.queue.enqueueRequest({
      operation,
      forward,
    });
  }
}

const authRefreshLink = (): AuthRefreshLink => {
  return new AuthRefreshLink();
};

export default authRefreshLink;
